//
// HighSocial TikTok profile lane — free, anonymous donor (no key, no nonce, no account).
//
//   POST https://www.highsocial.com/wp-admin/admin-ajax.php   (form-encoded)
//   action=get_user_engagement_data_tiktok&username=<handle>
//
// Measured live 2026-11-01: hit 1.8-3.3 s (8 handles, 8 of 8 HTTP 200), miss 0.8-1.4 s. The sibling
// action get_user_info_by_username answers the same profile but takes 8-11 s (a 79 KB body of 11 videos),
// far past the router's 5.5 s TikTok attempt cap, so this lane reads the engagement action only.
//
// Hit:  { success: true, data: { user: { stats, statsV2, user: { id, uniqueId, nickname, signature,
//        avatarThumb/Medium/Larger, verified, privateAccount } }, average_*: …, profile_pic: <base64> } }
// Miss: { success: false }   (get_user_info_by_username says { success: false, data: "user not found" })
// The donor strips spaces from a handle and answers for the stripped account, so the parser strips spaces (and
// a leading @, and case) on both sides before comparing the returned uniqueId with the handle asked for; any
// other mismatch is a lane failure, never somebody else's profile.
//
// Output = TiktokProfileProps, the shape the other TikTok lanes return (TikTokLiveAPI.processResponse).
//
// 🔴 Browser+edge-safe: fetch + AbortSignal only, no node:* imports. Type-only import of the barrel
//    (erased at build), so this file adds no runtime cycle.
//

import { RateLimited } from '../../router/hedge';
import { ProviderMiss, type Provider } from '../../router/registry';
import type { TiktokProfileProps } from '..';

export const HIGHSOCIAL_URL = 'https://www.highsocial.com/wp-admin/admin-ajax.php';
const UA = 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36';

const cleanHandle = (handle: string): string => handle.trim().replace(/^@/, '').replace(/\s+/g, '').toLowerCase();

/** Exact count when the donor sends it (statsV2, a string), else the rounded stats number. */
function count(v2: unknown, v1: unknown): number | undefined {
	const exact = typeof v2 === 'string' || typeof v2 === 'number' ? Number(v2) : NaN;
	if (Number.isFinite(exact)) return exact;
	return typeof v1 === 'number' && Number.isFinite(v1) ? v1 : undefined;
}

/** Donor body (parsed JSON) → profile. Throws ProviderMiss for an unknown account, Error for anything unusable. */
export function parseHighSocialProfile(body: unknown, handle: string): Exclude<TiktokProfileProps, false> {
	const b: any = body;
	if (!b || typeof b !== 'object') throw new Error('highsocial: non-JSON body');
	if (b.success === false) {
		// A miss carries no data at all (engagement action) or "user not found" (info action); any other reason is a failure.
		const reason = typeof b.data === 'string' ? b.data : b.data == null ? '' : JSON.stringify(b.data);
		if (!reason || /not found/i.test(reason)) throw new ProviderMiss('highsocial: no such user');
		throw new Error(`highsocial: ${reason.slice(0, 80)}`);
	}
	const user = b.data?.user?.user ?? b.data?.user_data?.user;
	const stats = b.data?.user?.stats ?? b.data?.user_data?.stats;
	const statsV2 = b.data?.user?.statsV2 ?? b.data?.user_data?.statsV2;
	if (b.success !== true || !user?.id || typeof user.uniqueId !== 'string')
		throw new Error('highsocial: reply without a user');
	if (cleanHandle(user.uniqueId) !== cleanHandle(handle)) throw new Error('highsocial: reply is for a different account');
	return {
		id: String(user.id),
		username: user.uniqueId,
		full_name: user.nickname,
		biography: user.signature,
		profile_pic_url: user.avatarMedium || user.avatarThumb,
		profile_pic_url_hd: user.avatarLarger || user.avatarMedium || user.avatarThumb,
		follower_count: count(statsV2?.followerCount, stats?.followerCount),
		following_count: count(statsV2?.followingCount, stats?.followingCount),
		likes_count: count(statsV2?.heartCount, stats?.heartCount ?? stats?.heart),
		media_count: count(statsV2?.videoCount, stats?.videoCount),
		is_verified: !!user.verified,
		is_private: !!user.privateAccount,
	};
}

/** One donor call. 429/503 + Retry-After → RateLimited (the breaker honours it); `signal` cancels the fetch and the body read. */
export async function fetchHighSocialProfile(handle: string, signal?: AbortSignal): Promise<Exclude<TiktokProfileProps, false>> {
	const username = cleanHandle(handle);
	if (!username) throw new ProviderMiss('highsocial: empty handle');
	const res = await fetch(HIGHSOCIAL_URL, {
		method: 'POST',
		headers: { 'User-Agent': UA, 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json, text/plain, */*' },
		body: new URLSearchParams({ action: 'get_user_engagement_data_tiktok', username }).toString(),
		signal,
	});
	if (res.status === 429 || res.status === 503) {
		const seconds = Number(res.headers.get('retry-after'));
		if (Number.isFinite(seconds) && seconds > 0) throw new RateLimited(seconds * 1000);
	}
	if (!res.ok) throw new Error(`highsocial: HTTP ${res.status}`);
	const text = await res.text();
	let body: unknown;
	try { body = JSON.parse(text); } catch { throw new Error(`highsocial: non-JSON reply (${text.slice(0, 60)})`); }
	return parseHighSocialProfile(body, username);
}

/** Free lane in the TikTok `profile` pool. Off: SOCIAL_PROV_HIGHSOCIAL_TT_ENABLED=false (default ON). */
export const HighSocialTikTokLane: Provider = {
	id: 'highsocial_tt',
	platform: 'tiktok',
	capabilities: ['profile'],
	family: 'own',
	transport: 'anonymous',
	requiresToken: false,
	seedLatencyMs: 2100,
	parser: (body) => body,
	execute: async (req, signal) => await fetchHighSocialProfile(req.query, signal),
};

export default HighSocialTikTokLane;
